// AccountShell.jsx — sidebar + topbar + mobile shell used across pages
import React from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Logo } from './Logo'
import { Ico } from './icons'
import { useLang } from '@/lib/lang'

// --- Desktop sidebar ---
export const Sidebar = ({ active, showLive = true }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useLang()
  const nav = t.acct.nav

  const NAV = [
    { id: "home",    label: nav.home,         icon: Ico.home,     to: "/dashboard" },
    { id: "visits",  label: nav.visits,       icon: Ico.calendar, to: "/manage/visits",      badge: "2" },
    { id: "track",   label: nav.active_visit, icon: Ico.truck,    to: "/manage/active-visit", live: true },
    { id: "docs",    label: nav.documents,    icon: Ico.doc,      to: "/manage/documents" },
    { id: "props",   label: nav.properties,   icon: Ico.building, to: "/manage/properties" },
    { id: "members", label: nav.membership,   icon: Ico.card,     to: "/manage/membership" },
    { id: "refer",   label: nav.refer,        icon: Ico.gift,     to: "/refer" },
  ]
  const NAV_SECONDARY = [
    { id: "profile", label: nav.profile,  icon: Ico.user, to: "/profile" },
    { id: "notifs",  label: nav.notifs,   icon: Ico.bell, to: "/notifications" },
    { id: "settings",label: nav.settings, icon: Ico.cog,  to: "/settings" },
  ]

  const isActive = (item) => {
    if (active !== undefined) return item.id === active
    return location.pathname.startsWith(item.to)
  }

  return (
    <aside className="sidebar">
      <div className="logo-row">
        <Link to="/dashboard" style={{ display: "contents" }}>
          <Logo variant="default" size={26} color="var(--cream)" accent="#E68A5C"/>
        </Link>
      </div>
      <div className="nav-eyebrow">{nav.casa}</div>
      {NAV.map(it => {
        const Icon = it.icon
        const active_item = isActive(it)
        return (
          <Link key={it.id} to={it.to} className={`nav-item ${active_item ? "active" : ""}`} style={{ textDecoration: "none", color: "inherit" }}>
            <Icon className="icon"/>
            <span>{it.label}</span>
            {it.badge && <span className="badge">{it.badge}</span>}
            {it.live && showLive && (
              <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 5 }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#7A8C6C", animation: "pulse-dot 2s infinite" }}/>
                <span className="font-mono" style={{ fontSize: 9, color: "#A6B59A" }}>{nav.live}</span>
              </span>
            )}
          </Link>
        )
      })}
      <div className="nav-divider"/>
      <div className="nav-eyebrow">{nav.account}</div>
      {NAV_SECONDARY.map(it => {
        const Icon = it.icon
        const active_item = isActive(it)
        return (
          <Link key={it.id} to={it.to} className={`nav-item ${active_item ? "active" : ""}`} style={{ textDecoration: "none", color: "inherit" }}>
            <Icon className="icon"/>
            <span>{it.label}</span>
          </Link>
        )
      })}
      <div className="sidebar-foot">
        <div className="avatar">ML</div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Maria Lopez</div>
          <div className="font-mono" style={{ fontSize: 10, color: "rgba(245,239,230,0.55)" }}>Casa · Coral Gables</div>
        </div>
        <button
          onClick={() => navigate('/login')}
          style={{ marginLeft: "auto", color: "rgba(245,239,230,0.5)", background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center" }}
        >
          <Ico.signout/>
        </button>
      </div>
    </aside>
  )
}

export const Topbar = ({ crumbs = ["Home"], showSearch = true, showNotif = true }) => {
  const navigate = useNavigate()
  const { lang, setLang, t } = useLang()
  const nav = t.acct.nav

  const Btn = ({ code }) => (
    <button
      onClick={() => setLang(code)}
      style={{
        padding: "5px 10px",
        fontFamily: "'Geist Mono', monospace",
        fontSize: 11,
        fontWeight: lang === code ? 700 : 500,
        background: lang === code ? "var(--cocoa)" : "transparent",
        color: lang === code ? "var(--cream)" : "var(--muted)",
        border: "none",
        borderRadius: 6,
        cursor: "pointer",
      }}
    >{code.toUpperCase()}</button>
  )

  return (
    <div className="topbar">
      <div className="crumbs">
        {crumbs.map((c, i) => (
          <React.Fragment key={i}>
            {i > 0 && <span style={{ opacity: 0.5 }}>/</span>}
            <span className={i === crumbs.length - 1 ? "here" : ""}>{c}</span>
          </React.Fragment>
        ))}
      </div>
      <div className="topbar-actions">
        {showSearch && (
          <div style={{ position: "relative" }}>
            <input className="auth-input" placeholder={nav.search_ph} style={{ padding: "9px 14px 9px 34px", fontSize: 13, width: 280, borderRadius: 999 }}/>
            <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--muted)" }}><Ico.search/></span>
          </div>
        )}
        <div style={{
          display: "flex", gap: 2, padding: 3, marginRight: 6,
          background: "rgba(58,42,31,0.05)",
          borderRadius: 9,
        }}>
          <Btn code="en"/>
          <Btn code="es"/>
        </div>
        {showNotif && (
          <Link to="/notifications" style={{ textDecoration: "none", color: "inherit" }}>
            <div className="icon-btn"><Ico.bell/><span className="dot"/></div>
          </Link>
        )}
        <button
          className="btn btn-primary"
          style={{ padding: "9px 16px", fontSize: 13 }}
          onClick={() => navigate('/#schedule')}
        >
          <Ico.plus width="14" height="14"/> {nav.book_visit}
        </button>
      </div>
    </div>
  )
}

// --- Mobile shell ---
const MobileStatus = ({ time = "9:41" }) => (
  <div className="mobile-statusbar">
    <span>{time}</span>
    <div className="dots">
      <svg width="16" height="10" viewBox="0 0 16 10" fill="currentColor"><rect x="0" y="6" width="3" height="4" rx=".5"/><rect x="4" y="4" width="3" height="6" rx=".5"/><rect x="8" y="2" width="3" height="8" rx=".5"/><rect x="12" y="0" width="3" height="10" rx=".5"/></svg>
      <svg width="14" height="10" viewBox="0 0 14 10" fill="none" stroke="currentColor" strokeWidth="1.2"><path d="M1 4a8 8 0 0112 0M3 6a5 5 0 018 0M5 8a2 2 0 014 0" fill="currentColor"/></svg>
      <svg width="22" height="10" viewBox="0 0 22 10" fill="none"><rect x=".5" y=".5" width="18" height="9" rx="2" stroke="currentColor"/><rect x="2.5" y="2.5" width="13" height="5" rx="1" fill="currentColor"/><rect x="19.5" y="3" width="1.5" height="4" rx=".5" fill="currentColor"/></svg>
    </div>
  </div>
)

export const MobileTabBar = ({ active = "home" }) => {
  const { t } = useLang()
  const nav = t.acct.nav
  const tabs = [
    { id: "home",    label: nav.home,    icon: Ico.home,     to: "/dashboard" },
    { id: "visits",  label: nav.visits,  icon: Ico.calendar, to: "/manage/visits" },
    { id: "book",    label: nav.book_visit, icon: Ico.plus,  to: "/#schedule" },
    { id: "docs",    label: nav.documents.slice(0,4), icon: Ico.doc, to: "/manage/documents" },
    { id: "profile", label: nav.profile, icon: Ico.user,     to: "/profile" },
  ]
  return (
    <div className="mobile-tabbar">
      {tabs.map(t => {
        const Icon = t.icon
        const isActive = t.id === active
        return (
          <Link key={t.id} to={t.to} className={`tab ${isActive ? "active" : ""}`} style={{ textDecoration: "none", color: "inherit" }}>
            {t.id === "book" ? (
              <div style={{ width: 36, height: 36, borderRadius: 12, background: "var(--terracotta)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--cream)", marginTop: -8, boxShadow: "0 6px 14px -4px rgba(194,85,45,0.5)" }}>
                <Ico.plus width="18" height="18"/>
              </div>
            ) : <Icon width="20" height="20"/>}
            <span>{t.label.toUpperCase()}</span>
          </Link>
        )
      })}
    </div>
  )
}

export const Phone = ({ children, status = "9:41" }) => (
  <div className="mobile-shell">
    <MobileStatus time={status}/>
    {children}
  </div>
)
